/**
 * CompareFlow - A/B Framing Experiment UI
 * Handles setup, running, and report stages for comparative focus group runs
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Play, Loader2, MessageCircle, Brain, TrendingUp, X,
  ChevronRight, AlertTriangle, Sparkles, Users
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { useTranslation } from '../i18n/LanguageContext';
import { useModels } from '../hooks/ModelsContext';

// ============================================================================
// Compare Setup
// ============================================================================

function FramingPanel({ label, framing, onUpdate }) {
  const { t } = useTranslation();

  const updateQuestion = (idx, val) => {
    const qs = [...(framing.questions || [''])];
    qs[idx] = val;
    onUpdate('questions', qs);
  };

  const addQuestion = () => {
    onUpdate('questions', [...(framing.questions || ['']), '']);
  };

  const removeQuestion = (idx) => {
    onUpdate('questions', (framing.questions || ['']).filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-stone-200 space-y-4">
      <h3 className="font-serif text-xl font-semibold text-stone-800">{label}</h3>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">{t('compare.topicLabel')}</label>
        <input
          type="text"
          value={framing.topic}
          onChange={e => onUpdate('topic', e.target.value)}
          className="input w-full"
          placeholder={t('setup.topicPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">{t('compare.descriptionLabel')}</label>
        <textarea
          value={framing.description}
          onChange={e => onUpdate('description', e.target.value)}
          className="input w-full"
          rows={2}
          placeholder={t('setup.descriptionPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">{t('compare.questionsLabel')}</label>
        {(framing.questions || ['']).map((q, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input
              type="text"
              value={q}
              onChange={e => updateQuestion(idx, e.target.value)}
              className="input flex-1"
              placeholder={`Question ${idx + 1}`}
            />
            {(framing.questions || ['']).length > 1 && (
              <button onClick={() => removeQuestion(idx)} className="text-stone-400 hover:text-red-500">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        <button onClick={addQuestion} className="text-sm text-amber-600 hover:text-amber-700">
          + Add question
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">{t('compare.briefingLabel')}</label>
        <textarea
          value={framing.moderator_briefing}
          onChange={e => onUpdate('moderator_briefing', e.target.value)}
          className="input w-full"
          rows={2}
          placeholder={t('setup.briefingPlaceholder')}
        />
      </div>
    </div>
  );
}

export function CompareSetup({ compare, config, personas, updateConfig, generatePersonas, isGeneratingPersonas }) {
  const { t } = useTranslation();
  const { models: MODELS } = useModels();

  const canStart = compare.framingA.topic && compare.framingB.topic && personas.length > 0;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('compare.title')}</h1>
        <p className="text-stone-500">{t('compare.subtitle')}</p>
      </div>

      {/* Two-column framing panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FramingPanel
          label={t('compare.framingA')}
          framing={compare.framingA}
          onUpdate={compare.updateFramingA}
        />
        <FramingPanel
          label={t('compare.framingB')}
          framing={compare.framingB}
          onUpdate={compare.updateFramingB}
        />
      </div>

      {/* Shared settings */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6">{t('compare.sharedSettings')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('session.roundsLabel', { n: config.rounds })}
            </label>
            <input
              type="range" min="3" max="20"
              value={config.rounds}
              onChange={e => updateConfig('rounds', parseInt(e.target.value))}
              className="w-full accent-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('session.moderatorModel')}
            </label>
            <select
              value={config.moderatorModel}
              onChange={e => updateConfig('moderatorModel', e.target.value)}
              className="input w-full"
            >
              {Object.entries(MODELS).map(([id, model]) => (
                <option key={id} value={id}>{model.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="compare-da"
              checked={config.devilsAdvocate}
              onChange={e => updateConfig('devilsAdvocate', e.target.checked)}
              className="w-4 h-4 accent-amber-500"
            />
            <label htmlFor="compare-da" className="text-sm font-medium text-stone-700">
              {t('session.devilsAdvocate')}
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="compare-ebt"
              checked={config.enhancedBeliefTracking}
              onChange={e => updateConfig('enhancedBeliefTracking', e.target.checked)}
              className="w-4 h-4 accent-indigo-500"
            />
            <label htmlFor="compare-ebt" className="text-sm font-medium text-stone-700">
              {t('session.enhancedBeliefTracking')}
            </label>
          </div>
        </div>
      </section>

      {/* Persona generation */}
      {personas.length === 0 ? (
        <div className="text-center">
          <button
            onClick={generatePersonas}
            disabled={isGeneratingPersonas}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            {isGeneratingPersonas ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t('demographics.generating')}</>
            ) : (
              <><Users className="w-4 h-4" /> {t('demographics.generate')}</>
            )}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 border border-stone-200">
          <h3 className="font-medium mb-3">{t('personas.title')} ({personas.length})</h3>
          <div className="flex flex-wrap gap-2">
            {personas.map(p => (
              <span key={p.id} className="px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-sm">
                {p.name}, {p.age}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Start button */}
      <div className="text-center">
        <button
          onClick={() => compare.startComparison(config, personas)}
          disabled={!canStart}
          className={`btn px-8 py-3 text-lg inline-flex items-center gap-3 ${
            canStart ? 'btn-primary' : 'bg-stone-200 text-stone-400 cursor-not-allowed'
          }`}
        >
          <Play className="w-5 h-5" />
          {t('compare.start')}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Compare Running
// ============================================================================

export function CompareRunning({ compare, personas }) {
  const { t } = useTranslation();
  const isA = compare.compareStage === 'running-a';
  const isB = compare.compareStage === 'running-b';
  const isGenerating = compare.compareStage === 'generating';
  const currentData = compare.sessionsData[isA ? 'A' : 'B'];

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="bg-white rounded-xl p-6 border border-stone-200">
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 ${isA ? 'text-amber-600' : isB || isGenerating ? 'text-green-600' : 'text-stone-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isA ? 'bg-amber-100' : 'bg-green-100'}`}>
              {isA ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            </div>
            <span className="font-medium">Session A</span>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-300" />
          <div className={`flex items-center gap-2 ${isB ? 'text-amber-600' : isGenerating ? 'text-green-600' : 'text-stone-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isB ? 'bg-amber-100' : isGenerating ? 'bg-green-100' : 'bg-stone-100'}`}>
              {isB ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            </div>
            <span className="font-medium">Session B</span>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-300" />
          <div className={`flex items-center gap-2 ${isGenerating ? 'text-amber-600' : 'text-stone-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isGenerating ? 'bg-amber-100' : 'bg-stone-100'}`}>
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </div>
            <span className="font-medium">
              {isGenerating ? t('compare.generating') : t('compare.comparisonSummary')}
            </span>
          </div>
        </div>
      </div>

      {/* Current session status */}
      <div className="bg-white rounded-xl p-6 border border-stone-200">
        <h3 className="font-serif text-xl mb-4">
          {isA ? t('compare.runningA') : isB ? t('compare.runningB') : t('compare.generating')}
        </h3>

        {compare.currentSpeaker && (
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
            <span className="text-stone-600">{compare.currentSpeaker.name}</span>
          </div>
        )}

        {compare.streamingContent && (
          <div className="bg-stone-50 rounded-lg p-4 text-sm text-stone-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
            {compare.streamingContent}
          </div>
        )}

        <div className="mt-4 text-sm text-stone-500">
          Messages: A={compare.sessionsData.A.messages.length} | B={compare.sessionsData.B.messages.length}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Compare Report
// ============================================================================

function SentimentChart({ messages, title }) {
  const { t } = useTranslation();
  const data = messages
    .filter(m => m.sentiment !== undefined)
    .map((m, idx) => ({ index: idx + 1, sentiment: m.sentiment, speaker: m.speaker, round: m.round }));

  if (data.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-6 border border-stone-200">
      <h3 className="font-serif text-lg mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis dataKey="index" stroke="#737373" fontSize={12} />
          <YAxis domain={[-1, 1]} stroke="#737373" fontSize={12} />
          <Tooltip content={({ active, payload }) => {
            if (active && payload && payload[0]) {
              const d = payload[0].payload;
              return (
                <div className="bg-white p-2 rounded shadow border border-stone-200 text-sm">
                  <p className="font-medium">{d.speaker}</p>
                  <p>Sentiment: {(d.sentiment * 100).toFixed(0)}%</p>
                </div>
              );
            }
            return null;
          }} />
          <Area type="monotone" dataKey="sentiment" stroke="#d97706" fill="#fef3c7" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function TranscriptPanel({ messages, title }) {
  const [expanded, setExpanded] = useState(false);
  const displayMessages = expanded ? messages : messages.slice(0, 10);

  return (
    <div className="bg-white rounded-xl p-6 border border-stone-200">
      <h3 className="font-serif text-lg mb-4">{title} ({messages.length})</h3>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {displayMessages.map((m, idx) => (
          <div key={m.id || idx} className="border-l-2 border-amber-300 pl-3 py-1">
            <div className="flex items-center gap-2 text-xs text-stone-500">
              <span className="font-medium text-stone-700">{m.speaker}</span>
              <span>R{m.round}</span>
            </div>
            <p className="text-sm text-stone-700 mt-1">{m.content}</p>
          </div>
        ))}
      </div>
      {messages.length > 10 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-sm text-amber-600 hover:text-amber-700"
        >
          {expanded ? 'Show less' : `Show all ${messages.length} messages`}
        </button>
      )}
    </div>
  );
}

export function CompareReport({ compare, personas }) {
  const { t } = useTranslation();
  const analysis = compare.comparisonAnalysis;
  const dataA = compare.sessionsData.A;
  const dataB = compare.sessionsData.B;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('compare.comparisonSummary')}</h1>
      </div>

      {/* Framing Effect */}
      {analysis?.framing_effect && (
        <section className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-8 text-white">
          <h2 className="font-serif text-2xl mb-4">{t('compare.framingEffect')}</h2>
          <p className="text-stone-300 leading-relaxed">{analysis.framing_effect}</p>
          {analysis.sentiment_comparison && (
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-sm text-stone-400">Session A</div>
                <div className="text-lg mt-1">{analysis.sentiment_comparison.session_a}</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-sm text-stone-400">Session B</div>
                <div className="text-lg mt-1">{analysis.sentiment_comparison.session_b}</div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Side-by-side sentiment charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SentimentChart messages={dataA.messages} title={t('compare.sentimentA')} />
        <SentimentChart messages={dataB.messages} title={t('compare.sentimentB')} />
      </div>

      {/* Key Differences & Similarities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {analysis?.key_differences?.length > 0 && (
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
            <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {t('compare.keyDifferences')}
            </h2>
            <ul className="space-y-3">
              {analysis.key_differences.map((d, idx) => (
                <li key={idx} className="flex gap-2 text-stone-700">
                  <span className="text-red-400 mt-1">&#x2022;</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {analysis?.surprising_similarities?.length > 0 && (
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
            <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-500" />
              {t('compare.similarities')}
            </h2>
            <ul className="space-y-3">
              {analysis.surprising_similarities.map((s, idx) => (
                <li key={idx} className="flex gap-2 text-stone-700">
                  <span className="text-green-400 mt-1">&#x2022;</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Per-Persona Shifts */}
      {analysis?.per_persona_shifts?.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-600" />
            {t('compare.personaShifts')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.per_persona_shifts.map((shift, idx) => (
              <div key={idx} className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-stone-800">{shift.name}</div>
                  {(shift.trajectory_a || shift.trajectory_b) && (
                    <div className="flex gap-1.5 text-xs">
                      {shift.trajectory_a && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">A: {shift.trajectory_a}</span>
                      )}
                      {shift.trajectory_b && (
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">B: {shift.trajectory_b}</span>
                      )}
                    </div>
                  )}
                </div>
                {shift.biggest_dimension_shift && (
                  <div className="text-xs text-amber-700 bg-amber-100/60 rounded px-2 py-1 mt-2 font-mono">
                    {shift.biggest_dimension_shift}
                  </div>
                )}
                <p className="text-sm text-stone-600 mt-2">{shift.shift}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Dimension Comparison */}
      {analysis?.dimension_comparison?.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            {t('compare.dimensionComparison') || 'Dimension Comparison'}
          </h2>
          <div className="space-y-4">
            {analysis.dimension_comparison.map((dim, idx) => (
              <div key={idx} className="p-4 bg-stone-50 rounded-xl border border-stone-100">
                <div className="font-medium text-stone-800 capitalize mb-3">{dim.dimension}</div>
                <div className="flex gap-3 items-center mb-2">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-stone-500 mb-1">
                      <span>Session A</span>
                      <span>{typeof dim.avg_score_a === 'number' ? (dim.avg_score_a * 100).toFixed(0) + '%' : dim.avg_score_a}</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2.5">
                      <div className="bg-blue-500 h-2.5 rounded-full transition-all" style={{ width: `${(dim.avg_score_a || 0) * 100}%` }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-stone-500 mb-1">
                      <span>Session B</span>
                      <span>{typeof dim.avg_score_b === 'number' ? (dim.avg_score_b * 100).toFixed(0) + '%' : dim.avg_score_b}</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2.5">
                      <div className="bg-purple-500 h-2.5 rounded-full transition-all" style={{ width: `${(dim.avg_score_b || 0) * 100}%` }} />
                    </div>
                  </div>
                </div>
                {dim.interpretation && (
                  <p className="text-sm text-stone-600 mt-2">{dim.interpretation}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Group Dynamics Comparison */}
      {analysis?.group_dynamics_comparison && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            {t('compare.groupDynamics') || 'Group Dynamics'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {analysis.group_dynamics_comparison.convergence && (
              <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="text-sm font-medium text-green-800 mb-1">{t('compare.convergence') || 'Convergence'}</div>
                <p className="text-sm text-stone-600">{analysis.group_dynamics_comparison.convergence}</p>
              </div>
            )}
            {analysis.group_dynamics_comparison.polarization && (
              <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="text-sm font-medium text-red-800 mb-1">{t('compare.polarization') || 'Polarization'}</div>
                <p className="text-sm text-stone-600">{analysis.group_dynamics_comparison.polarization}</p>
              </div>
            )}
            {analysis.group_dynamics_comparison.coalition_differences && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="text-sm font-medium text-amber-800 mb-1">{t('compare.coalitions') || 'Coalitions'}</div>
                <p className="text-sm text-stone-600">{analysis.group_dynamics_comparison.coalition_differences}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recommendations */}
      {analysis?.recommendations?.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <Brain className="w-5 h-5 text-amber-600" />
            {t('compare.recommendations')}
          </h2>
          <ul className="space-y-2">
            {analysis.recommendations.map((r, idx) => (
              <li key={idx} className="flex gap-2 text-stone-700">
                <span className="text-amber-500">&#x2192;</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Transcripts side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TranscriptPanel messages={dataA.messages} title={t('compare.transcriptA')} />
        <TranscriptPanel messages={dataB.messages} title={t('compare.transcriptB')} />
      </div>
    </div>
  );
}
